/**
 * Service d'authentification SSO pour Shop
 * Utilise des cookies partagés avec App (.local.dev)
 * L'utilisateur se connecte via App et est automatiquement authentifié sur Shop
 */

import { redirectToLogin as ssoRedirectToLogin } from '../utils/ssoRedirect';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://api.local.dev:8080';

class AuthService {
  constructor() {
    this._user = null;
    this._isAuthenticated = false;
  }

  /**
   * Vérifie si l'utilisateur est authentifié via les cookies SSO
   * C'est la méthode principale pour Shop car le login se fait sur App
   */
  async checkAuthentication() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include' // Important: envoie les cookies SSO
      });

      const data = await response.json();
      
      if (response.ok && data.authenticated) {
        this._user = data.user;
        this._isAuthenticated = true;
        localStorage.setItem('user', JSON.stringify(data.user));
        return { authenticated: true, user: data.user };
      }

      this._user = null;
      this._isAuthenticated = false;
      localStorage.removeItem('user');
      return { authenticated: false };
    } catch (error) {
      console.error('Erreur vérification authentification:', error);
      this._user = null;
      this._isAuthenticated = false;
      return { authenticated: false };
    }
  }

  /**
   * Récupère le profil utilisateur
   */
  async getProfile() {
    const result = await this.checkAuthentication();
    if (!result.authenticated) {
      throw new Error('Non authentifié');
    }
    return { user: result.user };
  }

  /**
   * Redirige vers App pour le login
   * Shop ne gère pas directement le login, c'est fait via App (SSO)
   */
  redirectToLogin(returnUrl = window.location.href) {
    ssoRedirectToLogin(returnUrl);
  }

  /**
   * Déconnexion - supprime les cookies SSO
   */
  async logout() {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      this._user = null;
      this._isAuthenticated = false;
      localStorage.removeItem('user');
    }
  }

  /**
   * Rafraîchit le token d'accès
   */
  async refreshToken() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        await this.logout();
        throw new Error(data.error || 'Session expirée');
      }

      return data;
    } catch (error) {
      await this.logout();
      throw new Error(error.message || 'Erreur lors du rafraîchissement du token');
    }
  }

  /**
   * Récupère l'utilisateur stocké localement
   */
  getStoredUser() {
    if (this._user) {
      return this._user;
    }
    try {
      const user = localStorage.getItem('user');
      this._user = user ? JSON.parse(user) : null;
      return this._user;
    } catch (error) {
      return null;
    }
  }

  /**
   * Indique si l'utilisateur semble authentifié (basé sur le cache)
   */
  isLoggedIn() {
    return this.getStoredUser() !== null;
  }

  /**
   * Vérifie si l'utilisateur est un Creator
   */
  isCreator() {
    const user = this.getStoredUser();
    return user?.roles?.includes('creator') || false;
  }

  /**
   * Wrapper pour les requêtes API authentifiées avec gestion automatique du refresh
   */
  async authenticatedFetch(url, options = {}) {
    const fetchOptions = {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    let response = await fetch(url, fetchOptions);

    // Si 401, essayer de rafraîchir le token et réessayer
    if (response.status === 401) {
      try {
        await this.refreshToken();
        response = await fetch(url, fetchOptions);
      } catch (refreshError) {
        // Le refresh a échoué, rediriger vers login
        this.redirectToLogin();
        throw new Error('Session expirée, redirection vers la connexion');
    }
    }

    return response;
  }
}

export default new AuthService();




